// ============================================================
// BattlefieldOverlays - what a plan looks like before you commit to it.
//
// Two questions a tactical map should answer without anyone doing
// arithmetic: "how far can I get?" and "who does this blast catch?".
// Both are drawn on the ground plane, under the tokens, so they inform
// the picture rather than covering it.
// ============================================================
import { memo, useContext, useLayoutEffect, useMemo, type CSSProperties } from 'react';
import { GpuReachContext } from './display/displayFaces';
import type { GpuReach } from './display/displayFaces';
import clsx from '../utils/clsx';
import type { Camera } from '../engine/battlefield/projection';
import { hordeRadius } from './../engine/battlefield/tokens';
import { project, SCENE_VIEW } from '../engine/battlefield/projection';
import type { ReachableTile } from '../engine/battlefield/movement';
import { tileKey } from '../engine/battlefield/movement';
import { CoverScreens } from './display/CoverScreens';
import type { ProjectedScreen } from '../engine/display/coverScreens';
import { bandFor, reachDrawn, rippleDelay, type MovementBand, type ReachLook } from '../engine/battlefield/bands';
import { PING_TTL_MS, PING_TYPES, edgeArrow } from '../engine/battlefield/pings';
import type { Ping } from '../engine/battlefield/pings';

export type { MovementBand };
import styles from '../styles/Battlefield.module.css';
import { CaughtMarks, HazardMark } from './HazardMarks';

/**
 * Project a tile's four corners into a polygon, AT ITS OWN STOREY.
 *
 * A hard 0.02 put every band on the ground, so a character standing on a
 * roof had their reach painted on the floor beneath them and the map
 * showed a shape nobody could walk (v7.97.0). The fill has always known
 * each tile's height; it simply threw it away.
 */
function tilePoints(tile: ReachableTile, size: number, camera: Camera): string {
  const h = size / 2;
  const y = (tile.y ?? 0) + 0.02;
  return [
    { x: tile.x - h, z: tile.z - h },
    { x: tile.x + h, z: tile.z - h },
    { x: tile.x + h, z: tile.z + h },
    { x: tile.x - h, z: tile.z + h },
  ]
    .map((c) => project({ x: c.x, y, z: c.z }, camera, SCENE_VIEW))
    .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');
}

export interface MovementOverlayProps {
  tiles: ReachableTile[];
  tileSize: number;
  camera: Camera;
  /**
   * Concentric bands, furthest last. Each tile takes the colour of the
   * first band its cost fits inside, so one flood fill at the largest
   * budget draws all four - `ReachableTile.cost` was already carried.
   */
  bands?: MovementBand[] | null;
  /**
   * How loud it is drawn (engine/battlefield/movementBandLook): quiet hairlines (the default), full, or off - which
   * keeps only the DANGEROUS tiles: a hazard is never hidden by a look.
   */
  look?: ReachLook;
  /**
   * The take-cover cue (2026-10-25): each tile's worst cover against the actor's threats, keyed by `tileKey`, and
   * the cost the grading stops at (the Full band). Shaded tiles ripple in by path cost, then the 3/4 and out-of-sight
   * ones breathe; open ones stay unshaded. `ripple` alternates with each new grading so the ripple replays.
   */
  graded?: Map<string, string> | null;
  gradeMax?: number;
  ripple?: 'a' | 'b';
}

/**
 * Where this combatant can actually get to.
 *
 * Not a circle: a circle is a lie the moment there is a wall in it. The
 * shape comes from a shortest-path flood fill, so it bends around
 * obstacles and thins out over difficult ground exactly as movement does.
 *
 * ONE FILL, AND THE BANDS AS LINES. Four tinted fills laid over the
 * ground took their colour FROM the ground: a band over amber rough
 * going came out orange, over dark impassable ground it disappeared, and
 * the map looked as though the GM's paint meant something about movement.
 * A line is opaque, so it says the same thing over any colour, and the
 * painted ground shows through unaltered underneath.
 */
function MovementOverlayImpl(props: MovementOverlayProps) {
  const { tiles, tileSize, camera, bands, look = 'quiet', graded, gradeMax = 0, ripple } = props;
  const { shown, edges } = reachDrawn(look, tiles, bands, tileSize);
  if (!shown.length) return null;
  return (
    <g data-testid="movement-overlay" className={styles.overlay} data-look={look} data-ripple={ripple}>
      {shown.map((tile) => {
        // DANGEROUS STILL WINS. A hazard is more urgent than a distance,
        // and a tile that is both should say the thing that can hurt you.
        const dangerous = tile.terrain === 'dangerous';
        const band = !dangerous && bands?.length ? bandFor(tile.cost, bands) : null;
        return (
          <polygon
            key={`${tile.x},${tile.z}`}
            className={dangerous ? styles.reachDangerous : styles.reach}
            data-band={band || undefined}
            points={tilePoints(tile, tileSize, camera)}
            {...coverCellOf(tile, tileSize, graded, gradeMax)}
          />
        );
      })}
      {/* Drawn after every tile, so a boundary is never half-hidden by
          the fill of the band on the other side of it. */}
      {edges.map((edge, i) => {
        const ey = (edge.y ?? 0) + 0.03;
        const a = project({ x: edge.x1, y: ey, z: edge.z1 }, camera, SCENE_VIEW);
        const b = project({ x: edge.x2, y: ey, z: edge.z2 }, camera, SCENE_VIEW);
        return (
          <line
            key={`${edge.key}-${i}`}
            className={styles[`band${edge.key[0].toUpperCase()}${edge.key.slice(1)}`] || styles.bandEdge}
            data-band-edge={edge.key}
            x1={a.x.toFixed(1)}
            y1={a.y.toFixed(1)}
            x2={b.x.toFixed(1)}
            y2={b.y.toFixed(1)}
          />
        );
      })}
    </g>
  );
}

/**
 * The reach handed to the GPU map instead (perf overhaul 2b): what the layer needs, once per change of the reach or
 * its look - the camera is not among it, so an orbit hands nothing - and withdrawn when this goes.
 */
function ReachHandOff({ publish, ...props }: MovementOverlayProps & { publish: (reach: GpuReach | null) => void }) {
  const { tiles, tileSize, bands, look = 'quiet', graded, gradeMax = 0, ripple } = props;
  const reach = useMemo(
    () => ({ tiles, tileSize, bands, look, graded, gradeMax, ripple }),
    [tiles, tileSize, bands, look, graded, gradeMax, ripple],
  );
  useLayoutEffect(() => {
    publish(reach);
    return () => publish(null);
  }, [publish, reach]);
  return null;
}

/** The svg's overlay, or - while the GPU canvas draws the map's world - the hand-off to it. */
function MovementOverlayChooser(props: MovementOverlayProps) {
  const publish = useContext(GpuReachContext);
  return publish ? <ReachHandOff {...props} publish={publish} /> : <MovementOverlayImpl {...props} />;
}

export const MovementOverlay = memo(MovementOverlayChooser);

/** A graded tile's cover and its ripple delay (by path cost); nothing for an ungraded one. */
function coverCellOf(
  tile: ReachableTile,
  tileSize: number,
  graded: Map<string, string> | null | undefined,
  max: number,
) {
  const level = graded?.get(tileKey(tile.x, tile.z, tileSize));
  if (!level) return {};
  const delay = rippleDelay(tile.cost, max);
  return { 'data-cover': level, style: { '--cell-delay': `${delay}ms` } as CSSProperties };
}

/**
 * A ring on the ground at (x, z), `y` up, as a 24-gon - not an SVG circle:
 * the ground is foreshortened by the camera pitch, and a real circle would
 * only be right looking straight down.
 */
function groundRing(x: number, z: number, y: number, radius: number, camera: Camera): string {
  return Array.from({ length: 24 }, (_, i) => {
    const a = (i / 24) * Math.PI * 2;
    return project({ x: x + Math.cos(a) * radius, y, z: z + Math.sin(a) * radius }, camera, SCENE_VIEW);
  })
    .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
    .join(' ');
}

export interface MoveGhostProps {
  /** The marker as it is carried: where it would stand if released (useTokenDrag `carried`). */
  carried: { id: string; x: number; z: number; elevation: number; footprint: number };
  camera: Camera;
  /** The take-cover cue's screens where the ghost has settled (2026-10-25), far runs under the ring, near over it. */
  screens?: ProjectedScreen[];
  riseKey?: string;
  still?: boolean;
}

/**
 * THE GHOST (plan design-gaps, M2): a planned move before it is made. On
 * the display a drag carries this dashed ring along the route while the
 * marker itself stays put; the release commits, Esc lets go. On the ground
 * at the storey the carry is on, the marker's own footprint.
 */
function MoveGhostImpl({ carried, camera, screens, riseKey = '', still = false }: MoveGhostProps) {
  const ring = (
    <polygon
      data-testid="move-ghost"
      data-token-id={carried.id}
      className={styles.ghost}
      points={groundRing(carried.x, carried.z, carried.elevation + 0.04, carried.footprint, camera)}
    />
  );
  if (!screens?.length) return ring;
  return (
    <g data-testid="move-ghost-cover">
      <CoverScreens id="ghost" screens={screens} side="far" riseKey={riseKey} still={still} />
      {ring}
      <CoverScreens id="ghost" screens={screens} side="near" riseKey={riseKey} still={still} />
    </g>
  );
}

export const MoveGhost = memo(MoveGhostImpl);

export interface MovementVectorProps {
  /** The route, start first. Empty draws nothing. */
  path: ReachableTile[];
  /** What the chosen action allows, in metres. */
  budget: number;
  /** Notable ground the route crosses, worst first. */
  crossed: { terrain: string; metres: number }[];
  /** What the route went up and down - the part of a cost you cannot see. */
  vertical?: { climbed: number; dropped: number } | null;
  camera: Camera;
  /** In Auto: the same bands the reach is drawn in, so the label can
      name the action this route would cost instead of a budget nobody
      chose. */
  bands?: MovementBand[] | null;
  /** A ghost's route (plan design-gaps, M2): dashed until the release commits it. */
  ghost?: boolean;
  /** No label at the route's end: the take-cover cue's readout off the map carries the pace (2026-10-25). */
  noLabel?: boolean;
}

/** What each band is called when a route ends inside it. */
const BAND_WORDS: Record<string, string> = {
  half: 'a half move',
  full: 'a full move',
  charge: 'a charge',
  run: 'a run',
};

/** What a route costs, in words: against the chosen budget, or - in Auto - which action pays for it. */
function spentWords(cost: number, budget: number, bands?: MovementBand[] | null): string {
  if (!bands?.length) return `${cost.toFixed(1)} m of ${budget.toFixed(0)} m`;
  const band = bandFor(cost, bands);
  return `${cost.toFixed(1)} m - ${band ? BAND_WORDS[band] || band : 'further than a run'}`;
}

/**
 * Five centimetres, so float dust from a projected surface never produces
 * "climbed 0.0 m".
 */
const VERTICAL_DUST = 0.05;

/** What the route went up and down, in words - the part of a cost you cannot see. */
function verticalWords(vertical?: { climbed: number; dropped: number } | null): string[] {
  const parts: string[] = [];
  if ((vertical?.climbed ?? 0) > VERTICAL_DUST) parts.push(`climbed ${vertical!.climbed.toFixed(1)} m`);
  if ((vertical?.dropped ?? 0) > VERTICAL_DUST) parts.push(`dropped ${vertical!.dropped.toFixed(1)} m`);
  return parts;
}

/**
 * The label a route carries: what it costs, and what it makes you walk
 * through.
 *
 * WITH BANDS the question is different. Auto does not pick an action, so
 * "12.4 m of 6 m" would be measuring against a budget nobody chose; what
 * a GM wants there is which action would pay for it. Over the last band
 * it says so plainly, and the line still turns red.
 */
export function vectorLabel(
  cost: number,
  budget: number,
  crossed: { terrain: string; metres: number }[],
  bands?: MovementBand[] | null,
  vertical?: { climbed: number; dropped: number } | null,
): string {
  const spent = spentWords(cost, budget, bands);
  // THE VERTICAL COMES FIRST, before the ground crossed. It is the
  // surprising part of the number - a route that costs twenty metres to
  // cover eight of floor has done something, and this is where it says
  // what. Terrain stays last, worst-first, as it always was.
  const parts = [...verticalWords(vertical), ...crossed.map((c) => `${c.terrain} ${c.metres.toFixed(1)} m`)];
  if (!parts.length) return spent;
  return `${spent} · ${parts.join(' · ')}`;
}

/**
 * The route a dragged token would actually take, and what it costs.
 *
 * The WALKABLE route, not the straight line: it comes from the same flood
 * fill that draws the reachable area, so the line bends round a wall
 * exactly where the highlight does. Over budget it turns red and the drag
 * still commits - the GM is the authority, and a map that refuses a move
 * is a map that gets switched off.
 */
/** The route's stroke: red past the budget whatever it is; dashed for a ghost's. */
function vectorClass(over: boolean, ghost: boolean): string {
  if (over) return styles.vectorOver;
  return ghost ? styles.vectorGhost : styles.vector;
}

function MovementVectorImpl(props: MovementVectorProps) {
  const { path, budget, crossed, camera, bands, vertical, ghost = false, noLabel = false } = props;
  if (path.length < 2) return null;
  const points = path.map((step) => project({ x: step.x, y: 0.05, z: step.z }, camera, SCENE_VIEW));
  const cost = path[path.length - 1].cost;
  const over = cost > budget + 1e-9;
  const end = points[points.length - 1];

  return (
    <g
      data-testid="movement-vector"
      className={styles.overlay}
      data-over-budget={over ? 'true' : 'false'}
      data-climbed={(vertical?.climbed ?? 0) > 0.05 ? vertical!.climbed.toFixed(1) : undefined}
      data-dropped={(vertical?.dropped ?? 0) > 0.05 ? vertical!.dropped.toFixed(1) : undefined}
    >
      <polyline
        className={vectorClass(over, ghost)}
        points={points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
      />
      {/* BELOW the end of the route, not above it. The route ends under
          the marker being carried, and a marker's name is drawn above it
          - so the two sat on top of each other and neither could be
          read. The halo comes from the stylesheet. */}
      {!noLabel && (
        <text
          className={over ? styles.vectorLabelOver : styles.vectorLabel}
          data-testid="movement-vector-label"
          x={end.x}
          y={end.y + 22}
          textAnchor="middle"
        >
          {vectorLabel(cost, budget, crossed, bands, vertical)}
        </text>
      )}
    </g>
  );
}

export const MovementVector = memo(MovementVectorImpl);

export interface PingOverlayProps {
  /** The pings this screen holds; each one plays its fade once, from its first paint. */
  pings: Ping[];
  camera: Camera;
  /** Where a `going` ping is drawn FROM, by token id. */
  tokens?: Record<string, { x: number; z: number }>;
}

type ScreenPoint = { x: number; y: number };

/** The intent line, for "going here" - from where the pinger's marker stands. */
function PingIntent({
  from,
  p,
  colour,
  camera,
}: {
  from: { x: number; z: number };
  p: ScreenPoint;
  colour: string;
  camera: Camera;
}) {
  const start = project({ x: from.x, y: 0.05, z: from.z }, camera, SCENE_VIEW);
  return (
    <line
      className={styles.pingIntent}
      stroke={colour}
      x1={start.x.toFixed(1)}
      y1={start.y.toFixed(1)}
      x2={p.x.toFixed(1)}
      y2={p.y.toFixed(1)}
    />
  );
}

/** An off-screen ping, as an arrow at the edge of the frame pointing at it. */
function PingArrow({
  id,
  edge,
  colour,
}: {
  id: string;
  edge: { x: number; y: number; angle: number };
  colour: string;
}) {
  const turn = ((edge.angle * 180) / Math.PI).toFixed(1);
  return (
    <g
      data-testid={`ping-offscreen-${id}`}
      transform={`translate(${edge.x.toFixed(1)},${edge.y.toFixed(1)}) rotate(${turn})`}
    >
      <polygon className={styles.pingArrow} points="0,0 -22,-9 -22,9" fill={colour} />
    </g>
  );
}

/** The ring's radius when fully open, in viewBox units; the ripple (Battlefield.module.css) opens it from a sixth. */
const RING_OPEN = 50;

/**
 * The ring, the dot and the words. Sized in VIEWBOX units, which the
 * panel scales down - often by half: what looks generous in a unit test is
 * a smudge at the table (gotchas: SVG text does not scale with the panel).
 *
 * ONE RIPPLE, thinning as it goes out, over about half a second. A ring
 * tied to a long TTL crept; one that popped and stayed read as a mark the
 * GM had placed rather than something said. The words stand where the
 * ripple ends rather than riding it out: still text reads in half a second.
 */
function PingRing({ ping, p, colour, label }: { ping: Ping; p: ScreenPoint; colour: string; label: string }) {
  const x = p.x.toFixed(1);
  return (
    <>
      <circle className={styles.pingRing} cx={x} cy={p.y.toFixed(1)} r={RING_OPEN} stroke={colour} />
      <circle className={styles.pingDot} cx={x} cy={p.y.toFixed(1)} r={3} fill={colour} />
      <text className={styles.pingLabel} x={x} y={(p.y + RING_OPEN + 20).toFixed(1)} textAnchor="middle" fill={colour}>
        {ping.byName}: {label}
      </text>
      {ping.note && (
        <text className={styles.pingNote} x={x} y={(p.y + RING_OPEN + 38).toFixed(1)} textAnchor="middle">
          {ping.note}
        </text>
      )}
    </>
  );
}

/**
 * One ping. At the height it was aimed at: a ping on a rooftop belongs on
 * the rooftop, and drawing it at the floor would put it behind the very
 * thing it is pointing at. It grows as it fades: a ring that only faded
 * read as a rendering glitch, and one that only grew never went away.
 */
function PingMark({ ping, camera, tokens }: { ping: Ping; camera: Camera; tokens?: PingOverlayProps['tokens'] }) {
  const meta = PING_TYPES[ping.type];
  const p = project({ x: ping.x, y: (ping.y ?? 0) + 0.05, z: ping.z }, camera, SCENE_VIEW);
  const edge = edgeArrow(p, SCENE_VIEW);
  const from = ping.fromId ? tokens?.[ping.fromId] : undefined;
  return (
    <g data-testid={`ping-${ping.type}`} data-ping-id={ping.id} className={styles.pingMark}>
      {from && <PingIntent from={from} p={p} colour={meta.colour} camera={camera} />}
      {edge ? (
        <PingArrow id={ping.id} edge={edge} colour={meta.colour} />
      ) : (
        <PingRing ping={ping} p={p} colour={meta.colour} label={meta.label} />
      )}
    </g>
  );
}

/** The fade's length, handed to the stylesheet: `PING_TTL_MS` stays the one place the half-second is said. */
const PING_LIFE = { '--ping-life': `${PING_TTL_MS}ms` } as CSSProperties;

/**
 * What somebody is pointing at, and for how long.
 *
 * A ring that grows and fades, in the colour of what was said. Nothing
 * here is interactive: a ping is speech, and clicking it should do
 * exactly what clicking the ground under it does.
 *
 * THE BROWSER PLAYS THE FADE, from the ring's first paint (2026-10-31). A
 * React clock re-rendering the panel every 60 ms drove it before, from
 * the press: on a laptop slow to render, the two renders between the press
 * and the ring spent the half-second, and its player never saw their own
 * ping. A mark stays mounted, invisible, until its screen drops it.
 *
 * OFF-SCREEN PINGS BECOME AN ARROW at the edge of the frame. Every client
 * has its own camera, so "look here" is worth nothing when "here" is off
 * the side of somebody's screen.
 */
function PingOverlayImpl({ pings, camera, tokens }: PingOverlayProps) {
  if (!pings.length) return null;
  return (
    <g data-testid="ping-overlay" className={styles.overlay} style={PING_LIFE}>
      {pings.map((ping) => (
        <PingMark key={ping.id} ping={ping} camera={camera} tokens={tokens} />
      ))}
    </g>
  );
}

export const PingOverlay = memo(PingOverlayImpl);

export interface HordeAreasProps {
  /** Tokens that are hordes, with the magnitude left in each. */
  hordes: { id: string; x: number; z: number; y?: number; magnitude: number; footprint: number }[];
  camera: Camera;
}

/**
 * A horde is a CROWD, and a crowd takes up ground.
 *
 * Drawn as a translucent area whose radius goes by the square root of
 * magnitude, so the area is proportional to the bodies in it - and
 * shrinks, visibly, as the horde is ground down. The ordinary token stays
 * underneath: this is a layer over data combat already keeps, so nothing
 * about targeting a horde changes.
 *
 * A ring on the ground (`groundRing`), for the same reason the blast
 * template is a polygon.
 */
function HordeAreasImpl({ hordes, camera }: HordeAreasProps) {
  if (!hordes.length) return null;
  return (
    <g data-testid="horde-areas" className={styles.overlay}>
      {hordes.map((horde) => {
        const radius = hordeRadius(horde.magnitude, horde.footprint);
        if (radius <= 0) return null;
        // JUST ABOVE THE GROUND THEY ARE STANDING ON - which is not
        // always y=0. This was hardcoded, so a horde on a gantry drew
        // its area down in the courtyard below them (v8.39.0).
        const y = (horde.y ?? 0) + 0.04;
        const middle = project({ x: horde.x, y, z: horde.z }, camera, SCENE_VIEW);
        return (
          <g key={horde.id} data-testid={`horde-area-${horde.id}`} data-magnitude={horde.magnitude}>
            <polygon className={styles.horde} points={groundRing(horde.x, horde.z, y, radius, camera)} />
            <text className={styles.hordeLabel} x={middle.x} y={middle.y} textAnchor="middle">
              {horde.magnitude}
            </text>
          </g>
        );
      })}
    </g>
  );
}

export const HordeAreas = memo(HordeAreasImpl);

export interface BlastOverlayProps {
  centre: { x: number; z: number } | null;
  radius: number;
  camera: Camera;
  /** Names of everyone inside, for the label. */
  caught: string[];
  /**
   * Where they are standing, so the ring can point at them.
   *
   * The count alone was fine while the template did nothing. Now that it
   * decides who takes damage, "3 caught" is a claim worth being able to
   * check before pulling the trigger - especially the ones you did not
   * mean to include.
   */
  caughtAt?: { x: number; z: number }[];
  /** Each victim's avoid test, as a chip under their mark (T2: `avoidChip`); none when the template has none. */
  avoid?: string | null;
  /** The dice behind a size (v8.372.5): "1d5+1" - drawn as "≈ 4 m (1d5+1)". */
  sizeHint?: string;
}

/**
 * The footprint of a Blast(X) weapon, and who is standing in it.
 *
 * Drawn as a 24-gon rather than an SVG circle because the ground plane
 * is foreshortened by the camera pitch - a real circle would only be
 * right when looking straight down.
 */
function BlastOverlayImpl({ centre, radius, camera, caught, caughtAt, sizeHint, avoid }: BlastOverlayProps) {
  if (!centre || radius <= 0) return null;
  const ring = groundRing(centre.x, centre.z, 0.03, radius, camera);
  const middle = project({ x: centre.x, y: 0.03, z: centre.z }, camera, SCENE_VIEW);

  return (
    <g data-testid="blast-overlay" className={styles.overlay}>
      <polygon className={styles.blast} points={ring} />
      <CaughtMarks at={caughtAt} camera={camera} testId="blast-caught-marker" avoid={avoid} />
      <text className={styles.blastLabel} x={middle.x} y={middle.y} textAnchor="middle">
        {sizeHint ? `≈ ${radius} m (${sizeHint})` : `${radius} m`}
        {caught.length ? ` - ${caught.length} caught` : ''}
      </text>
    </g>
  );
}

export const BlastOverlay = memo(BlastOverlayImpl);

/**
 * WHO THE TEMPLATE WOULD CATCH, drawn at their feet.
 *
 * A plain circle read as decoration - it needs to read as a HAZARD. So: a
 * warning triangle over a grill of bars, the grammar of tape on a factory
 * floor, in the amber the blast ring already uses. Small enough not to hide
 * the marker it stands under.
 */
export interface ConeOverlayProps {
  /** The hand holding the flamer. */
  apex: { x: number; z: number } | null;
  /** Degrees, 0 = due east, matching `bearingBetween`. */
  bearing: number;
  /** How far it reaches, in metres - the weapon's own range. */
  length: number;
  /** TOTAL included angle. The cone spreads half of it either side. */
  degrees: number;
  camera: Camera;
  caught: string[];
  caughtAt?: { x: number; z: number }[];
  /** Each victim's avoid test, as a chip under their mark (T2: `avoidChip`); none when the template has none. */
  avoid?: string | null;
  /** Overridden so a declared kill zone is findable apart from a flamer's cone. */
  testId?: string;
  /** The dice behind the sizes (v8.372.5). */
  sizeHint?: string;
}

/**
 * The footprint of a Spray weapon.
 *
 * The arc is walked in steps rather than drawn as an SVG arc for the same
 * reason the blast ring is a 24-gon: the ground plane is foreshortened by the
 * camera pitch, so anything curved has to be projected point by point.
 */
function ConeOverlayImpl({
  apex,
  bearing,
  length,
  degrees,
  camera,
  caught,
  caughtAt,
  testId = 'cone-overlay',
  sizeHint,
  avoid,
}: ConeOverlayProps) {
  if (!apex || length <= 0 || degrees <= 0) return null;
  const half = (Math.min(360, degrees) / 2) * (Math.PI / 180);
  const axis = bearing * (Math.PI / 180);
  const STEPS = 16;
  const arc = Array.from({ length: STEPS + 1 }, (_, i) => {
    const a = axis - half + (i / STEPS) * half * 2;
    return project({ x: apex.x + Math.cos(a) * length, y: 0.03, z: apex.z + Math.sin(a) * length }, camera, SCENE_VIEW);
  });
  const tip = project({ x: apex.x, y: 0.03, z: apex.z }, camera, SCENE_VIEW);
  const points = [tip, ...arc].map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const label = project(
    { x: apex.x + Math.cos(axis) * length * 0.6, y: 0.03, z: apex.z + Math.sin(axis) * length * 0.6 },
    camera,
    SCENE_VIEW,
  );

  return (
    <g data-testid={testId} className={styles.overlay}>
      <polygon className={styles.blast} points={points} />
      <CaughtMarks at={caughtAt} camera={camera} testId="cone-caught-marker" avoid={avoid} />
      <text className={styles.blastLabel} x={label.x} y={label.y} textAnchor="middle">
        {sizeHint ? '≈ ' : ''}
        {length} m · {degrees}°{sizeHint ? ` (${sizeHint})` : ''} {caught.length ? `- ${caught.length} caught` : ''}
      </text>
    </g>
  );
}

export const ConeOverlay = memo(ConeOverlayImpl);

export interface BeamOverlayProps {
  apex: { x: number; z: number } | null;
  /** Degrees, 0 = due east, matching `bearingBetween`. */
  bearing: number;
  /** How far the beam reaches, in metres. */
  length: number;
  camera: Camera;
  caught: string[];
  caughtAt?: { x: number; z: number }[];
  /** Each victim's avoid test, as a chip under their mark (T2: `avoidChip`); none when the template has none. */
  avoid?: string | null;
  /** The beam's half-width in metres - matches `combatantsAlongBeam`. */
  halfWidthM?: number;
  /** The dice behind the sizes (v8.372.5). */
  sizeHint?: string;
}

/**
 * The footprint of a Beam weapon (v8.292.0): a lance of ground from the
 * muzzle to the weapon's reach, one metre each side - the projected
 * rectangle of exactly the strip `combatantsAlongBeam` tests, so the
 * preview IS the shot, the blast-ring rule again.
 */
function BeamOverlayImpl({
  apex,
  bearing,
  length,
  camera,
  caught,
  caughtAt,
  halfWidthM = 1,
  sizeHint,
  avoid,
}: BeamOverlayProps) {
  if (!apex || length <= 0) return null;
  const axis = bearing * (Math.PI / 180);
  const ax = Math.cos(axis);
  const az = Math.sin(axis);
  // Perpendicular unit vector for the width.
  const px = -az;
  const pz = ax;
  const corners = [
    { x: apex.x + px * halfWidthM, z: apex.z + pz * halfWidthM },
    { x: apex.x + ax * length + px * halfWidthM, z: apex.z + az * length + pz * halfWidthM },
    { x: apex.x + ax * length - px * halfWidthM, z: apex.z + az * length - pz * halfWidthM },
    { x: apex.x - px * halfWidthM, z: apex.z - pz * halfWidthM },
  ].map((c) => project({ x: c.x, y: 0.03, z: c.z }, camera, SCENE_VIEW));
  const points = corners.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const label = project({ x: apex.x + ax * length * 0.6, y: 0.03, z: apex.z + az * length * 0.6 }, camera, SCENE_VIEW);

  return (
    <g data-testid="beam-overlay" className={styles.overlay}>
      <polygon className={styles.blast} points={points} />
      <CaughtMarks at={caughtAt} camera={camera} testId="beam-caught-marker" avoid={avoid} />
      <text className={styles.blastLabel} x={label.x} y={label.y} textAnchor="middle">
        {sizeHint ? '≈ ' : ''}
        {length} m beam{sizeHint ? ` (${sizeHint})` : ''} {caught.length ? `- ${caught.length} caught` : ''}
      </text>
    </g>
  );
}

export const BeamOverlay = memo(BeamOverlayImpl);

export interface ArcOverlayProps {
  /** The aimed target's feet - where the lightning starts. */
  primary: { x: number; z: number } | null;
  /** Metres per leap - the ring drawn around each link. */
  reach: number;
  /** The predicted chain, the primary first, at their feet. */
  chain: { x: number; z: number }[];
  camera: Camera;
  /** Names of the echoes, for the label. */
  caught: string[];
  sizeHint?: string;
}

/**
 * The arc (v8.372.5): a reach ring around the aimed target and the path
 * the lightning would take - primary → leap 1 → leap 2 - with a hazard
 * mark and its leap number at each echo. The preview is the shot: the
 * chain comes from `arcChain` on the expected sizes, the same walk the
 * attack makes with the rolled ones.
 *
 * A FAINT REMINDER (2026-10-21: "the Arc orange ring is distracting"):
 * the ring has no fill, a thin faint dash and a small label, and the
 * chain is a line - it wore the blast template's class, whose fill won
 * over its `fill="none"` and drew an amber triangle. The hazard marks and
 * the leap numbers keep their weight: they are who gets hit.
 */
function ArcOverlayImpl({ primary, reach, chain, camera, caught, sizeHint }: ArcOverlayProps) {
  if (!primary || reach <= 0) return null;
  const ring = groundRing(primary.x, primary.z, 0.03, reach, camera);
  // A leap to a figure behind the eye is not drawn, as that figure is not (CaughtMarks); the numbers keep their leap.
  const path = chain.map((at, leap) => ({ leap, ...project({ x: at.x, y: 0.04, z: at.z }, camera, SCENE_VIEW) }));
  const shown = path.filter((p) => !p.behind);
  const middle = project({ x: primary.x, y: 0.03, z: primary.z }, camera, SCENE_VIEW);
  const size = sizeHint ? `≈ ${reach} m (${sizeHint})` : `${reach} m`;
  return (
    <g data-testid="arc-overlay" className={styles.overlay}>
      <polygon className={styles.arcRing} data-testid="arc-ring" points={ring} />
      {shown.length > 1 && (
        <polyline
          className={styles.arcChain}
          data-testid="arc-chain"
          points={shown.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
        />
      )}
      {shown
        .filter((p) => p.leap > 0)
        .map((p) => (
          <g key={p.leap}>
            <HazardMark x={p.x} y={p.y} testId="arc-caught-marker" />
            <text className={styles.blastLabel} x={p.x + 12} y={p.y - 8}>
              {p.leap}
            </text>
          </g>
        ))}
      <text className={clsx(styles.blastLabel, styles.arcLabel)} x={middle.x} y={middle.y + 14} textAnchor="middle">
        {size} arc{caught.length ? ` - ${caught.length} caught` : ''}
      </text>
    </g>
  );
}

export const ArcOverlay = memo(ArcOverlayImpl);

export interface SpawnedOverlayProps {
  /** The ground each spawned copy covers, in world metres - `spawnedOutlines`. */
  outlines: { spawnerId: string; outline: { x: number; z: number }[] }[];
  /** Where the spawned victims stand. */
  caughtAt: { x: number; z: number }[];
  /** Each victim's avoid test, as a chip under their mark (T2: `avoidChip`); none when the template has none. */
  avoid?: string | null;
  camera: Camera;
}

/**
 * The SECONDARY template (v8.373.5), drawn faintly under the primary: one
 * outline per victim predicted to spawn a copy, and a hazard mark on
 * everyone those copies would catch. No label per copy - five blasts each
 * saying "2 m" is noise; the count rides on the marks. A PREDICTION in one
 * more way than the primary: it assumes nobody steps clear.
 */
function SpawnedOverlayImpl({ outlines, caughtAt, camera, avoid }: SpawnedOverlayProps) {
  if (!outlines.length) return null;
  return (
    <g data-testid="area-spawn-preview" className={styles.overlay} opacity={0.45}>
      {outlines.map(({ spawnerId, outline }) => (
        <polygon
          key={spawnerId}
          className={styles.blast}
          data-testid="area-spawn-outline"
          points={outline
            .map((at) => project({ x: at.x, y: 0.03, z: at.z }, camera, SCENE_VIEW))
            .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
            .join(' ')}
        />
      ))}
      <CaughtMarks at={caughtAt} camera={camera} testId="area-spawn-caught-marker" avoid={avoid} />
    </g>
  );
}

export const SpawnedOverlay = memo(SpawnedOverlayImpl);
